import React, { useRef, useState } from 'react';
import { Share2, MessageSquare, Mail, Link as LinkIcon, Check, Facebook, Instagram, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const WhatsApp = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M17.5 14.4c-.3-.2-1.8-.9-2-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-.9 1.1-.2.2-.3.2-.6.1-.3-.2-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.4-.5c.2-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.2.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.3-.7.3-1.3.2-1.4-.1-.2-.3-.2-.6-.4zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2z" />
  </svg>
);
const XLogo = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.9 2H22l-7.5 8.6L23 22h-6.9l-5.4-7-6.2 7H1.4l8-9.2L1 2h7l4.9 6.4L18.9 2zm-1.2 18h1.9L7.4 3.9H5.3L17.7 20z" />
  </svg>
);

export const ShareButtons = ({ url, title, text, imageUrl, compact = false, testId = 'share' }) => {
  const [copied, setCopied] = useState(false);
  const [igBusy, setIgBusy] = useState(false);
  const igBlob = useRef(null);

  // Prefetch the story image on hover/touch so share() can run inside the click's user activation (Safari).
  const prefetchInstagram = () => {
    if (!imageUrl || igBlob.current) return;
    igBlob.current = fetch(imageUrl).then((r) => { if (!r.ok) throw new Error('image'); return r.blob(); }).catch(() => { igBlob.current = null; return null; });
  };
  const absUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
  const message = `${title}${text ? ` — ${text}` : ''}`;
  const enc = encodeURIComponent;

  const nativeShare = async () => {
    try {
      await navigator.share({ title, text: message, url: absUrl });
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error('Sharing was cancelled');
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(absUrl);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  // Instagram has no web share intent: hand the story-sized card image to the
  // native share sheet (mobile) or download it + copy the link (desktop).
  const downloadAndCopy = async (blob, fileName) => {
    const dl = document.createElement('a');
    dl.href = URL.createObjectURL(blob);
    dl.download = fileName;
    document.body.appendChild(dl);
    dl.click();
    document.body.removeChild(dl);
    setTimeout(() => URL.revokeObjectURL(dl.href), 5000);
    try { await navigator.clipboard.writeText(`${message} ${absUrl}`); } catch { /* clipboard optional */ }
    toast.success('Story image saved', { description: 'Open Instagram, add it to your Story or post, and paste the copied link in your caption or bio.' });
  };

  const shareInstagram = async () => {
    if (!imageUrl) return;
    setIgBusy(true);
    try {
      prefetchInstagram();
      const blob = await igBlob.current;
      if (!blob) throw new Error('image');
      const file = new File([blob], `${(title || 'devotional').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title, text: `${message} ${absUrl}` });
          return;
        } catch (e) {
          if (e?.name === 'AbortError') return;
          // NotAllowedError (lost user activation) / TypeError → fall back to download.
        }
      }
      await downloadAndCopy(blob, file.name);
    } catch (e) {
      toast.error('Could not prepare the Instagram image');
    } finally {
      setIgBusy(false);
    }
  };

  const links = [
    { key: 'sms', label: 'Text', Icon: MessageSquare, href: `sms:?&body=${enc(`${message} ${absUrl}`)}` },
    { key: 'whatsapp', label: 'WhatsApp', Icon: WhatsApp, href: `https://wa.me/?text=${enc(`${message} ${absUrl}`)}` },
    { key: 'facebook', label: 'Facebook', Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(absUrl)}` },
    { key: 'x', label: 'X', Icon: XLogo, href: `https://twitter.com/intent/tweet?text=${enc(message)}&url=${enc(absUrl)}` },
    { key: 'email', label: 'Email', Icon: Mail, href: `mailto:?subject=${enc(title)}&body=${enc(`${message}\n\n${absUrl}`)}` },
  ];

  const btn = `inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-church-green hover:text-church-green transition-colors ${compact ? 'px-2.5 py-1 text-xs' : 'px-3.5 py-1.5 text-sm'}`;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`${testId}-buttons`}>
      {typeof navigator !== 'undefined' && navigator.share && (
        <button type="button" onClick={nativeShare} className={`${btn} !bg-church-green !text-white !border-church-green hover:opacity-90`} data-testid={`${testId}-native`}>
          <Share2 className="w-4 h-4" /> Share
        </button>
      )}
      {imageUrl && (
        <button type="button" onClick={shareInstagram} onMouseEnter={prefetchInstagram} onTouchStart={prefetchInstagram} onFocus={prefetchInstagram} disabled={igBusy} className={btn} title="Save a Story-sized image for Instagram" data-testid={`${testId}-instagram`}>
          {igBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Instagram className="w-4 h-4" />} Instagram
        </button>
      )}
      {links.map(({ key, label, Icon, href }) => (
        <a key={key} href={href} target={key === 'sms' || key === 'email' ? undefined : '_blank'} rel="noopener noreferrer" className={btn} data-testid={`${testId}-${key}`}>
          <Icon className="w-4 h-4" /> {label}
        </a>
      ))}
      <button type="button" onClick={copy} className={btn} data-testid={`${testId}-copy`}>
        {copied ? <Check className="w-4 h-4 text-church-green" /> : <LinkIcon className="w-4 h-4" />} {copied ? 'Copied' : 'Copy link'}
      </button>
    </div>
  );
};
